import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  BeakerIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  CircleStackIcon,
  ClockIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  ShieldCheckIcon,
  StopCircleIcon,
} from '@heroicons/react/24/outline';
import {
  useCancelEvidenceEmbeddingRun,
  useCancelEvidenceEmbeddingSchedule,
  useCreateEvidenceEmbeddingSchedule,
  useEvidenceEmbeddingPolicy,
  useEvidenceEmbeddingRuns,
  useEvidenceEmbeddingSchedules,
  usePreviewEvidenceEmbeddingRun,
  useStartEvidenceEmbeddingRun,
  useUpdateEvidenceEmbeddingPolicy,
} from '../../hooks/useAdminDashboard';
import { useMailboxList } from '../../hooks/useMailboxes';
import type {
  EmailEvidenceEmbeddingBudget,
  EmailEvidenceEmbeddingCadence,
  EmailEvidenceEmbeddingModel,
  EmailEvidenceEmbeddingPolicy,
  EmailEvidenceEmbeddingRun,
  EmailEvidenceEmbeddingSchedule,
  EmailEvidenceEmbeddingScheduleStatus,
  EmailEvidenceEmbeddingScope,
  EmailEvidenceEmbeddingScopeKind,
} from '../../types/admin.types';

type ScopeFormState = {
  kind: EmailEvidenceEmbeddingScopeKind;
  mailboxId: string;
  folderId: string;
  matterId: string;
  clientId: string;
  messageIds: string;
  threadIds: string;
  linkedOnly: boolean;
};

type ScheduleFormState = {
  name: string;
  status: EmailEvidenceEmbeddingScheduleStatus;
  cadence: EmailEvidenceEmbeddingCadence;
  nextRunAt: string;
};

const DEFAULT_SCOPE_FORM: ScopeFormState = {
  kind: 'tenant_linked',
  mailboxId: '',
  folderId: '',
  matterId: '',
  clientId: '',
  messageIds: '',
  threadIds: '',
  linkedOnly: true,
};

const DEFAULT_SCHEDULE_FORM: ScheduleFormState = {
  name: 'Evidence indexing standard',
  status: 'paused',
  cadence: 'manual',
  nextRunAt: '',
};

function n(value: string | number | null | undefined): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function formatNumber(value: string | number | null | undefined): string {
  return new Intl.NumberFormat().format(n(value));
}

function formatUsd(value: string | number | null | undefined): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  }).format(n(value));
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function listFromText(value: string): string[] {
  return value
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function buildScope(form: ScopeFormState): EmailEvidenceEmbeddingScope {
  const scope: EmailEvidenceEmbeddingScope = {
    kind: form.kind,
    linkedOnly: form.linkedOnly,
  };

  if (form.mailboxId) scope.mailboxId = form.mailboxId;
  if (form.folderId) scope.folderId = form.folderId;
  if (form.matterId) scope.matterId = form.matterId;
  if (form.clientId) scope.clientId = form.clientId;

  const messageIds = listFromText(form.messageIds);
  const threadIds = listFromText(form.threadIds);
  if (messageIds.length > 0) scope.messageIds = messageIds;
  if (threadIds.length > 0) scope.threadIds = threadIds;

  return scope;
}

function estimateRunDuration(run: EmailEvidenceEmbeddingRun): string {
  const jobs = n(run.estimated_jobs);
  const attachments = n(run.estimated_attachments);
  const hashHydration = n(run.skip_counts?.attachmentsNeedingHashHydration);
  const tokens = n(run.estimated_embedding_tokens);

  if (jobs <= 0) return 'No processing expected';

  const baseMinutes = Math.max(2, jobs * 0.15);
  const attachmentMinutes = attachments * 0.35;
  const hydrationMinutes = hashHydration * 0.2;
  const tokenMinutes = tokens / 1_500_000;
  const lowMinutes = Math.max(5, Math.ceil(baseMinutes + attachmentMinutes + hydrationMinutes + tokenMinutes));
  const highMinutes = Math.ceil(lowMinutes * 2.2);

  if (lowMinutes < 60) return `${lowMinutes}-${highMinutes} min`;

  const lowHours = Math.max(1, Math.floor(lowMinutes / 60));
  const highHours = Math.max(lowHours + 1, Math.ceil(highMinutes / 60));
  return `${lowHours}-${highHours} hr`;
}

function humanizeRunLabel(value: string): string {
  const labelMap: Record<string, string> = {
    scopedMessages: 'Messages found',
    eligibleMessages: 'Eligible messages',
    alreadyIndexedBodies: 'Already indexed bodies',
    alreadyIndexedAttachments: 'Already indexed attachments',
    attachmentsNeedingHashHydration: 'Attachments needing hash hydration',
    skippedDeleted: 'Deleted messages',
    skippedUnlinked: 'Unlinked messages',
    skippedPrivileged: 'Privileged messages',
    skippedRetentionExpired: 'Retention expired',
    skippedMissingBodyHash: 'Missing body content',
    skippedMissingAttachmentStorage: 'Missing attachment storage',
    max_messages_per_run: 'Message cap exceeded',
    max_attachments_per_run: 'Attachment cap exceeded',
    max_jobs_per_run: 'Job cap exceeded',
    max_embedding_tokens_per_run: 'Token budget exceeded',
    max_estimated_cost_usd: 'Cost cap exceeded',
  };

  if (labelMap[value]) return labelMap[value];
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function budgetFromPolicy(policy?: EmailEvidenceEmbeddingPolicy): EmailEvidenceEmbeddingBudget {
  return {
    maxMessagesPerRun: policy?.maxMessagesPerRun ?? 250,
    maxAttachmentsPerRun: policy?.maxAttachmentsPerRun ?? 250,
    maxJobsPerRun: policy?.maxJobsPerRun ?? 500,
    maxEmbeddingTokensPerRun: policy?.maxEmbeddingTokensPerRun ?? 5000000,
    maxEstimatedCostUsd: policy?.maxEstimatedCostUsd ?? 1,
    requireApprovalAboveUsd: policy?.requireApprovalAboveUsd ?? 0.25,
    queuePressureMaxWaiting: policy?.queuePressureMaxWaiting ?? 1000,
  };
}

function errorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Request failed';
  const maybeResponse = error as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return maybeResponse.response?.data?.error || maybeResponse.response?.data?.message || maybeResponse.message || 'Request failed';
}

const statusStyles: Record<string, string> = {
  enabled: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
  disabled: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  previewed: 'bg-lex-accent-soft text-lex-accent',
  queued: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  running: 'bg-lex-accent-soft text-lex-accent',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  partial: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  const normalizedStatus = status || 'unknown';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[normalizedStatus] || statusStyles.disabled}`}>
      {normalizedStatus.replace(/_/g, ' ')}
    </span>
  );
};

function canCancelRun(run: EmailEvidenceEmbeddingRun): boolean {
  if (run.status === 'previewed') return true;
  if (!['queued', 'running', 'partial'].includes(run.status)) return false;
  const progress = run.run_progress;
  if (!progress || progress.total === 0) return true;
  const active = Number(progress.queued || 0) + Number(progress.running || 0) + Number(progress.retry_scheduled || 0);
  return active > 0;
}

function runCreatedJobCount(run: EmailEvidenceEmbeddingRun): number {
  return Number(run.queued_body_jobs || 0) + Number(run.queued_attachment_jobs || 0);
}

function runProgressLabel(run: EmailEvidenceEmbeddingRun): string {
  const progress = run.run_progress;
  if (!progress || progress.total === 0) return '-';
  const active = Number(progress.queued || 0) + Number(progress.running || 0) + Number(progress.retry_scheduled || 0);
  const completed = Number(progress.completed || 0);
  const failed = Number(progress.failed || 0);
  if (failed > 0) return `${formatNumber(completed)} done / ${formatNumber(failed)} failed`;
  if (active > 0) return `${formatNumber(completed)} done / ${formatNumber(active)} active`;
  return `${formatNumber(completed)} done`;
}

function runOutcomeLabel(run: EmailEvidenceEmbeddingRun): string {
  const progress = run.run_progress;
  if (!progress || progress.total === 0) {
    if (run.status === 'previewed') return 'Preview only. No jobs started.';
    return 'No job-level status has been recorded for this run.';
  }

  const active = Number(progress.queued || 0) + Number(progress.running || 0) + Number(progress.retry_scheduled || 0);
  if (active > 0) {
    return `${formatNumber(progress.completed)} completed, ${formatNumber(active)} still active.`;
  }
  if (Number(progress.failed || 0) > 0 || Number(progress.blocked || 0) > 0) {
    return `${formatNumber(progress.completed)} completed; ${formatNumber(Number(progress.failed || 0) + Number(progress.blocked || 0))} need attention.`;
  }
  return `${formatNumber(progress.completed)} completed successfully.`;
}

const Panel: React.FC<React.PropsWithChildren<{ title: string; icon: React.ComponentType<{ className?: string }>; aside?: React.ReactNode }>> = ({
  title,
  icon: Icon,
  aside,
  children,
}) => (
  <section className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <div className="flex items-center gap-2">
        <div className="rounded-lg bg-lex-accent-soft p-2 text-lex-accent">
          <Icon className="h-5 w-5" />
        </div>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
      </div>
      {aside}
    </div>
    {children}
  </section>
);

const FieldLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <label className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{children}</label>
);

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition-colors placeholder:text-gray-400 focus:border-lex-accent focus:ring-2 focus:ring-lex-accent/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white';

const EvidenceIndexingView: React.FC = () => {
  const policyQuery = useEvidenceEmbeddingPolicy();
  const schedulesQuery = useEvidenceEmbeddingSchedules();
  const runsQuery = useEvidenceEmbeddingRuns(50);
  const mailboxQuery = useMailboxList({ page: 1, pageSize: 100, status: 'active', sortBy: 'email', sortOrder: 'asc' });
  const updatePolicy = useUpdateEvidenceEmbeddingPolicy();
  const createSchedule = useCreateEvidenceEmbeddingSchedule();
  const previewRun = usePreviewEvidenceEmbeddingRun();
  const startRun = useStartEvidenceEmbeddingRun();
  const cancelRun = useCancelEvidenceEmbeddingRun();
  const cancelSchedule = useCancelEvidenceEmbeddingSchedule();

  const [policyDraft, setPolicyDraft] = useState<EmailEvidenceEmbeddingPolicy | null>(null);
  const [scopeForm, setScopeForm] = useState<ScopeFormState>(DEFAULT_SCOPE_FORM);
  const [scheduleForm, setScheduleForm] = useState<ScheduleFormState>(DEFAULT_SCHEDULE_FORM);
  const [selectedPreview, setSelectedPreview] = useState<EmailEvidenceEmbeddingRun | null>(null);
  const [startConfirmationRun, setStartConfirmationRun] = useState<EmailEvidenceEmbeddingRun | null>(null);

  useEffect(() => {
    if (policyQuery.data) setPolicyDraft(policyQuery.data);
  }, [policyQuery.data]);

  const policy = policyDraft || policyQuery.data;
  const mailboxes = mailboxQuery.data?.mailboxes || [];
  const latestRun = runsQuery.data?.[0];
  const activeSchedules = (schedulesQuery.data || []).filter((schedule) => schedule.status === 'enabled').length;
  const failedRuns = (runsQuery.data || []).filter((run) => run.status === 'failed').length;
  const caps = budgetFromPolicy(policy);

  const previewDisabled = previewRun.isPending || !policy?.isEnabled;
  const scope = useMemo(() => buildScope(scopeForm), [scopeForm]);

  const updatePolicyField = <K extends keyof EmailEvidenceEmbeddingPolicy>(
    key: K,
    value: EmailEvidenceEmbeddingPolicy[K],
  ) => {
    setPolicyDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const handleSavePolicy = () => {
    if (!policyDraft) return;
    updatePolicy.mutate({
      policy: {
        isEnabled: policyDraft.isEnabled,
        linkedOnly: policyDraft.linkedOnly,
        allowPrivileged: policyDraft.allowPrivileged,
        includeInlineAttachments: policyDraft.includeInlineAttachments,
        defaultEmbeddingModel: policyDraft.defaultEmbeddingModel,
        embeddingVersion: policyDraft.embeddingVersion,
        pipelineVersion: policyDraft.pipelineVersion,
        scheduleCadence: policyDraft.scheduleCadence,
        requirePreview: policyDraft.requirePreview,
        maxMessagesPerRun: Number(policyDraft.maxMessagesPerRun),
        maxAttachmentsPerRun: Number(policyDraft.maxAttachmentsPerRun),
        maxJobsPerRun: Number(policyDraft.maxJobsPerRun),
        maxEmbeddingTokensPerRun: Number(policyDraft.maxEmbeddingTokensPerRun),
        maxEstimatedCostUsd: Number(policyDraft.maxEstimatedCostUsd),
        requireApprovalAboveUsd: Number(policyDraft.requireApprovalAboveUsd),
        queuePressureMaxWaiting: Number(policyDraft.queuePressureMaxWaiting),
        costSafetyMultiplier: Number(policyDraft.costSafetyMultiplier),
      },
    });
  };

  const handlePreview = () => {
    previewRun.mutate(
      {
        scope,
        runKind: 'preview',
        embeddingModel: policy?.defaultEmbeddingModel,
        embeddingVersion: policy?.embeddingVersion,
        pipelineVersion: policy?.pipelineVersion,
        caps,
      },
      {
        onSuccess: (run) => setSelectedPreview(run),
      },
    );
  };

  const handleCreateSchedule = () => {
    createSchedule.mutate({
      name: scheduleForm.name.trim() || 'Evidence indexing standard',
      status: scheduleForm.status,
      cadence: scheduleForm.cadence,
      scope,
      caps,
      nextRunAt: scheduleForm.nextRunAt || null,
    });
  };

  const handleStart = (run: EmailEvidenceEmbeddingRun) => {
    setStartConfirmationRun(run);
  };

  const handleConfirmStart = () => {
    if (!startConfirmationRun) return;
    startRun.mutate(startConfirmationRun.run_id, {
      onSuccess: (updated) => {
        setSelectedPreview(updated);
        setStartConfirmationRun(null);
      },
    });
  };

  const handleCancelRun = (run: EmailEvidenceEmbeddingRun) => {
    cancelRun.mutate(run.run_id, {
      onSuccess: (updated) => setSelectedPreview(updated),
    });
  };

  const handleCancelSchedule = (schedule: EmailEvidenceEmbeddingSchedule) => {
    cancelSchedule.mutate(schedule.schedule_id);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Evidence Indexing</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Email embeddings, scan standards, guarded previews, and run history.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            policyQuery.refetch();
            schedulesQuery.refetch();
            runsQuery.refetch();
          }}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <MetricCard icon={ShieldCheckIcon} label="Policy" value={policy?.isEnabled ? 'Enabled' : 'Disabled'} tone={policy?.isEnabled ? 'green' : 'red'} />
        <MetricCard icon={CalendarDaysIcon} label="Active Schedules" value={activeSchedules} tone="blue" />
        <MetricCard icon={CpuChipIcon} label="Cost Cap" value={formatUsd(policy?.maxEstimatedCostUsd)} tone="amber" />
        <MetricCard icon={ExclamationTriangleIcon} label="Failed Runs" value={failedRuns} tone={failedRuns > 0 ? 'red' : 'green'} />
      </div>

      {(policyQuery.error || updatePolicy.error || createSchedule.error || previewRun.error || startRun.error || cancelRun.error || cancelSchedule.error) && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          {errorMessage(policyQuery.error || updatePolicy.error || createSchedule.error || previewRun.error || startRun.error || cancelRun.error || cancelSchedule.error)}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.7fr)]">
        <Panel title="Firm Standards" icon={ShieldCheckIcon} aside={policyQuery.isLoading ? <span className="text-xs text-gray-500">Loading</span> : null}>
          <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2">
            <ToggleField
              label="Evidence indexing"
              checked={!!policy?.isEnabled}
              onChange={(value) => updatePolicyField('isEnabled', value)}
            />
            <ToggleField
              label="Linked mail only"
              checked={!!policy?.linkedOnly}
              onChange={(value) => updatePolicyField('linkedOnly', value)}
            />
            <ToggleField
              label="Allow privileged mail"
              checked={!!policy?.allowPrivileged}
              onChange={(value) => updatePolicyField('allowPrivileged', value)}
            />
            <ToggleField
              label="Inline attachments"
              checked={!!policy?.includeInlineAttachments}
              onChange={(value) => updatePolicyField('includeInlineAttachments', value)}
            />

            <div className="space-y-1.5">
              <FieldLabel>Embedding Model</FieldLabel>
              <select
                className={inputClass}
                value={policy?.defaultEmbeddingModel || 'text-embedding-3-large'}
                onChange={(event) => updatePolicyField('defaultEmbeddingModel', event.target.value as EmailEvidenceEmbeddingModel)}
              >
                <option value="text-embedding-3-large">text-embedding-3-large</option>
                <option value="text-embedding-3-small">text-embedding-3-small</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <FieldLabel>Default Cadence</FieldLabel>
              <select
                className={inputClass}
                value={policy?.scheduleCadence || 'manual'}
                onChange={(event) => updatePolicyField('scheduleCadence', event.target.value as EmailEvidenceEmbeddingCadence)}
              >
                <option value="manual">Manual</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>

            <NumberField label="Max messages" value={policy?.maxMessagesPerRun} onChange={(value) => updatePolicyField('maxMessagesPerRun', value)} />
            <NumberField label="Max attachments" value={policy?.maxAttachmentsPerRun} onChange={(value) => updatePolicyField('maxAttachmentsPerRun', value)} />
            <NumberField label="Max jobs" value={policy?.maxJobsPerRun} onChange={(value) => updatePolicyField('maxJobsPerRun', value)} />
            <NumberField label="Token budget" value={policy?.maxEmbeddingTokensPerRun} onChange={(value) => updatePolicyField('maxEmbeddingTokensPerRun', value)} />
            <NumberField label="Cost cap USD" step="0.01" value={policy?.maxEstimatedCostUsd} onChange={(value) => updatePolicyField('maxEstimatedCostUsd', value)} />
            <NumberField label="Approval threshold USD" step="0.01" value={policy?.requireApprovalAboveUsd} onChange={(value) => updatePolicyField('requireApprovalAboveUsd', value)} />
            <NumberField label="Queue pressure cap" value={policy?.queuePressureMaxWaiting} onChange={(value) => updatePolicyField('queuePressureMaxWaiting', value)} />
            <NumberField label="Cost multiplier" step="0.01" value={policy?.costSafetyMultiplier} onChange={(value) => updatePolicyField('costSafetyMultiplier', value)} />

            <div className="space-y-1.5 lg:col-span-2">
              <FieldLabel>Embedding Version</FieldLabel>
              <input
                className={inputClass}
                value={policy?.embeddingVersion || ''}
                onChange={(event) => updatePolicyField('embeddingVersion', event.target.value)}
              />
            </div>
            <div className="space-y-1.5 lg:col-span-2">
              <FieldLabel>Pipeline Version</FieldLabel>
              <input
                className={inputClass}
                value={policy?.pipelineVersion || ''}
                onChange={(event) => updatePolicyField('pipelineVersion', event.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center justify-end border-t border-gray-200 px-4 py-3 dark:border-gray-700">
            <button
              type="button"
              onClick={handleSavePolicy}
              disabled={!policyDraft || updatePolicy.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-lex-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-lex-accent-hover disabled:opacity-50"
            >
              <CheckCircleIcon className="h-4 w-4" />
              Save Standards
            </button>
          </div>
        </Panel>

        <Panel title="Scan Scope" icon={MagnifyingGlassIcon}>
          <div className="space-y-4 p-4">
            <div className="space-y-1.5">
              <FieldLabel>Scope</FieldLabel>
              <select
                className={inputClass}
                value={scopeForm.kind}
                onChange={(event) => setScopeForm((current) => ({ ...current, kind: event.target.value as EmailEvidenceEmbeddingScopeKind }))}
              >
                <option value="tenant_linked">All linked mailboxes</option>
                <option value="mailbox_linked">One linked mailbox</option>
                <option value="folder">Folder</option>
                <option value="matter">Matter</option>
                <option value="message_list">Message list</option>
                <option value="thread_list">Thread list</option>
              </select>
            </div>

            {['mailbox_linked', 'folder'].includes(scopeForm.kind) && (
              <div className="space-y-1.5">
                <FieldLabel>Mailbox</FieldLabel>
                <select
                  className={inputClass}
                  value={scopeForm.mailboxId}
                  onChange={(event) => setScopeForm((current) => ({ ...current, mailboxId: event.target.value }))}
                >
                  <option value="">Select mailbox</option>
                  {mailboxes.map((mailbox) => (
                    <option key={mailbox.mailbox_id} value={mailbox.mailbox_id}>
                      {mailbox.email}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {scopeForm.kind === 'folder' && (
              <TextField label="Folder ID" value={scopeForm.folderId} onChange={(value) => setScopeForm((current) => ({ ...current, folderId: value }))} />
            )}
            {scopeForm.kind === 'matter' && (
              <TextField label="Matter ID" value={scopeForm.matterId} onChange={(value) => setScopeForm((current) => ({ ...current, matterId: value }))} />
            )}
            {scopeForm.kind === 'message_list' && (
              <TextAreaField label="Message IDs" value={scopeForm.messageIds} onChange={(value) => setScopeForm((current) => ({ ...current, messageIds: value }))} />
            )}
            {scopeForm.kind === 'thread_list' && (
              <TextAreaField label="Thread IDs" value={scopeForm.threadIds} onChange={(value) => setScopeForm((current) => ({ ...current, threadIds: value }))} />
            )}

            <ToggleField
              label="Linked only"
              checked={scopeForm.linkedOnly}
              onChange={(value) => setScopeForm((current) => ({ ...current, linkedOnly: value }))}
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                disabled={previewDisabled}
                onClick={handlePreview}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-lex-accent bg-white px-3 py-2 text-sm font-medium text-lex-accent transition-colors hover:bg-lex-state-hover disabled:opacity-50 dark:bg-gray-800"
              >
                <BeakerIcon className="h-4 w-4" />
                Preview
              </button>
              <button
                type="button"
                disabled={createSchedule.isPending || !scheduleForm.name.trim()}
                onClick={handleCreateSchedule}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-lex-accent px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-lex-accent-hover disabled:opacity-50"
              >
                <CalendarDaysIcon className="h-4 w-4" />
                Save Schedule
              </button>
            </div>
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <Panel title="Schedule Editor" icon={CalendarDaysIcon}>
          <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
            <TextField label="Name" value={scheduleForm.name} onChange={(value) => setScheduleForm((current) => ({ ...current, name: value }))} />
            <div className="space-y-1.5">
              <FieldLabel>Status</FieldLabel>
              <select
                className={inputClass}
                value={scheduleForm.status}
                onChange={(event) => setScheduleForm((current) => ({ ...current, status: event.target.value as EmailEvidenceEmbeddingScheduleStatus }))}
              >
                <option value="paused">Paused</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <FieldLabel>Cadence</FieldLabel>
              <select
                className={inputClass}
                value={scheduleForm.cadence}
                onChange={(event) => setScheduleForm((current) => ({ ...current, cadence: event.target.value as EmailEvidenceEmbeddingCadence }))}
              >
                <option value="manual">Manual</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <FieldLabel>Next Run</FieldLabel>
              <input
                type="datetime-local"
                className={inputClass}
                value={scheduleForm.nextRunAt}
                onChange={(event) => setScheduleForm((current) => ({ ...current, nextRunAt: event.target.value }))}
              />
            </div>
          </div>
          <ScheduleList
            schedules={schedulesQuery.data || []}
            onCancel={handleCancelSchedule}
            cancellingId={cancelSchedule.variables}
            cancelling={cancelSchedule.isPending}
          />
        </Panel>

        <Panel title="Latest Preview" icon={BeakerIcon}>
          <PreviewSummary
            run={selectedPreview || latestRun || null}
            onStart={handleStart}
            onCancel={handleCancelRun}
            starting={startRun.isPending}
            cancelling={cancelRun.isPending}
          />
        </Panel>
      </div>

      <Panel title="Run History" icon={ClockIcon}>
        <RunHistory
          runs={runsQuery.data || []}
          onStart={handleStart}
          onCancel={handleCancelRun}
          starting={startRun.isPending}
          cancelling={cancelRun.isPending}
        />
      </Panel>

      {startConfirmationRun && (
        <StartRunConfirmationModal
          run={startConfirmationRun}
          starting={startRun.isPending}
          startError={startRun.error}
          onCancel={() => {
            if (!startRun.isPending) setStartConfirmationRun(null);
          }}
          onConfirm={handleConfirmStart}
        />
      )}
    </div>
  );
};

const MetricCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  tone: 'blue' | 'green' | 'amber' | 'red';
}> = ({ icon: Icon, label, value, tone }) => {
  const tones = {
    blue: 'bg-lex-accent-soft text-lex-accent',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className={`mb-3 inline-flex rounded-lg p-2 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-semibold text-gray-900 dark:text-white">{value}</p>
      <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{label}</p>
    </div>
  );
};

const ToggleField: React.FC<{ label: string; checked: boolean; onChange: (checked: boolean) => void }> = ({
  label,
  checked,
  onChange,
}) => (
  <label className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{label}</span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
    />
  </label>
);

const NumberField: React.FC<{
  label: string;
  value: number | undefined;
  step?: string;
  onChange: (value: number) => void;
}> = ({ label, value, step = '1', onChange }) => (
  <div className="space-y-1.5">
    <FieldLabel>{label}</FieldLabel>
    <input
      type="number"
      min="0"
      step={step}
      className={inputClass}
      value={value ?? 0}
      onChange={(event) => onChange(Number(event.target.value))}
    />
  </div>
);

const TextField: React.FC<{ label: string; value: string; onChange: (value: string) => void }> = ({
  label,
  value,
  onChange,
}) => (
  <div className="space-y-1.5">
    <FieldLabel>{label}</FieldLabel>
    <input className={inputClass} value={value} onChange={(event) => onChange(event.target.value)} />
  </div>
);

const TextAreaField: React.FC<{ label: string; value: string; onChange: (value: string) => void }> = ({
  label,
  value,
  onChange,
}) => (
  <div className="space-y-1.5">
    <FieldLabel>{label}</FieldLabel>
    <textarea
      rows={4}
      className={`${inputClass} resize-y`}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

const ScheduleList: React.FC<{
  schedules: EmailEvidenceEmbeddingSchedule[];
  onCancel: (schedule: EmailEvidenceEmbeddingSchedule) => void;
  cancelling: boolean;
  cancellingId?: string;
}> = ({ schedules, onCancel, cancelling, cancellingId }) => (
  <div className="border-t border-gray-200 dark:border-gray-700">
    {schedules.length === 0 ? (
      <div className="p-6 text-center text-sm text-gray-600 dark:text-gray-400">No schedules</div>
    ) : (
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {schedules.slice(0, 6).map((schedule) => (
          <div key={schedule.schedule_id} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{schedule.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {schedule.cadence} · {schedule.scope?.kind || 'scope'} · next {formatDate(schedule.next_run_at)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <StatusBadge status={schedule.status} />
              {schedule.status !== 'disabled' && (
                <button
                  type="button"
                  disabled={cancelling && cancellingId === schedule.schedule_id}
                  onClick={() => onCancel(schedule)}
                  className="inline-flex items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-900/20"
                >
                  <StopCircleIcon className="h-3.5 w-3.5" />
                  Disable
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const PreviewSummary: React.FC<{
  run: EmailEvidenceEmbeddingRun | null;
  onStart: (run: EmailEvidenceEmbeddingRun) => void;
  onCancel: (run: EmailEvidenceEmbeddingRun) => void;
  starting: boolean;
  cancelling: boolean;
}> = ({ run, onStart, onCancel, starting, cancelling }) => {
  if (!run) {
    return (
      <div className="flex min-h-72 items-center justify-center p-6 text-center">
        <div>
          <CircleStackIcon className="mx-auto h-10 w-10 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">No preview selected</p>
        </div>
      </div>
    );
  }

  const skipEntries = Object.entries(run.skip_counts || {}).filter(([, value]) => Number(value) > 0);
  const scopedMessages = Number(run.skip_counts?.scopedMessages || 0);
  const eligibleMessages = Number(run.skip_counts?.eligibleMessages || 0);
  const alreadyIndexedBodies = Number(run.skip_counts?.alreadyIndexedBodies || 0);
  const alreadyIndexedAttachments = Number(run.skip_counts?.alreadyIndexedAttachments || 0);
  const pendingJobs = Number(run.estimated_jobs || 0);
  const canStart = run.status === 'previewed' && pendingJobs > 0;
  const cancellable = canCancelRun(run);

  return (
    <div className="p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusBadge status={run.status} />
            <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(run.created_at)}</span>
          </div>
          <p className="mt-2 truncate text-sm font-medium text-gray-900 dark:text-white">{run.scope?.kind || 'scope'} preview</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {run.status === 'previewed' && (
            <button
              type="button"
              disabled={starting || !canStart}
              onClick={() => onStart(run)}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-lex-accent px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-lex-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PlayIcon className="h-4 w-4" />
              Start
            </button>
          )}
          {cancellable && (
            <button
              type="button"
              disabled={cancelling}
              onClick={() => onCancel(run)}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/50 dark:bg-gray-800 dark:text-red-300 dark:hover:bg-red-900/20"
            >
              <StopCircleIcon className="h-4 w-4" />
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MiniStat label="Message Bodies" value={formatNumber(run.estimated_messages)} />
        <MiniStat label="Attachments" value={formatNumber(run.estimated_attachments)} />
        <MiniStat label="Jobs" value={formatNumber(run.estimated_jobs)} />
        <MiniStat label="Cost" value={formatUsd(run.estimated_cost_usd)} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <MiniStat label="Tokens" value={formatNumber(run.estimated_embedding_tokens)} />
        <MiniStat label="Queue Waiting" value={formatNumber(run.queue_metrics?.waiting)} />
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
        {pendingJobs > 0 ? (
          <span>
            {formatNumber(scopedMessages)} message{scopedMessages === 1 ? '' : 's'} found; {formatNumber(eligibleMessages)} eligible; {formatNumber(pendingJobs)} new indexing job{pendingJobs === 1 ? '' : 's'} ready.
            {' '}Includes {formatNumber(run.estimated_messages)} message bod{Number(run.estimated_messages) === 1 ? 'y' : 'ies'} and {formatNumber(run.estimated_attachments)} attachment{Number(run.estimated_attachments) === 1 ? '' : 's'}.
          </span>
        ) : (
          <span>
            {formatNumber(scopedMessages)} message{scopedMessages === 1 ? '' : 's'} found; {formatNumber(eligibleMessages)} eligible; no new jobs. Indexed bodies: {formatNumber(alreadyIndexedBodies)}; indexed attachments: {formatNumber(alreadyIndexedAttachments)}.
          </span>
        )}
      </div>

      {run.cap_hit_reason && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          {humanizeRunLabel(run.cap_hit_reason)}
        </div>
      )}

      <div className="mt-4">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Skipped</p>
        {skipEntries.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">None</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {skipEntries.map(([key, value]) => (
              <span key={key} className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                {humanizeRunLabel(key)}: {formatNumber(value)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MiniStat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
    <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
  </div>
);

const StartRunConfirmationModal: React.FC<{
  run: EmailEvidenceEmbeddingRun;
  starting: boolean;
  startError?: unknown;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ run, starting, startError, onCancel, onConfirm }) => {
  const cost = n(run.estimated_cost_usd);
  const costCap = n(run.caps?.maxEstimatedCostUsd);
  const threshold = n(run.caps?.requireApprovalAboveUsd);
  const tokens = n(run.estimated_embedding_tokens);
  const tokenCap = n(run.caps?.maxEmbeddingTokensPerRun);
  const queueWaiting = n(run.queue_metrics?.waiting);
  const queueActive = n(run.queue_metrics?.active);
  const hashHydration = n(run.skip_counts?.attachmentsNeedingHashHydration);
  const storageSkipped = n(run.skip_counts?.skippedMissingAttachmentStorage);
  const requiresApproval = cost >= threshold;
  const costCapExceeded = costCap > 0 && cost > costCap;
  const tokenCapExceeded = tokenCap > 0 && tokens > tokenCap;
  const blockedByHardCap = costCapExceeded || tokenCapExceeded;
  const canStart = !blockedByHardCap && n(run.estimated_jobs) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/55 px-4 py-6">
      <div className="w-full max-w-2xl rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-amber-50 p-2 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
              <ExclamationTriangleIcon className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Start Evidence Indexing Run</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                This queues message and attachment jobs for the selected scope.
              </p>
            </div>
          </div>
          <StatusBadge status={starting ? 'queued' : run.status} />
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <MiniStat label="Message Bodies" value={formatNumber(run.estimated_messages)} />
            <MiniStat label="Attachments" value={formatNumber(run.estimated_attachments)} />
            <MiniStat label="Jobs" value={formatNumber(run.estimated_jobs)} />
            <MiniStat label="Est. Cost" value={formatUsd(run.estimated_cost_usd)} />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <MiniStat label="Tokens" value={formatNumber(run.estimated_embedding_tokens)} />
            <MiniStat label="Runtime Estimate" value={estimateRunDuration(run)} />
            <MiniStat label="Queue" value={`${formatNumber(queueWaiting)} waiting / ${formatNumber(queueActive)} active`} />
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-900">
            <div className="grid grid-cols-1 gap-2 text-sm text-gray-700 dark:text-gray-300 sm:grid-cols-2">
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Scope:</span> {run.scope?.kind || 'scope'}
              </div>
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Approval threshold:</span> {formatUsd(threshold)}
              </div>
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Cost cap:</span> {formatUsd(costCap)}
              </div>
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Token budget:</span> {formatNumber(tokenCap)}
              </div>
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Hash hydration:</span> {formatNumber(hashHydration)} attachment{hashHydration === 1 ? '' : 's'}
              </div>
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Missing storage:</span> {formatNumber(storageSkipped)}
              </div>
            </div>
          </div>

          {blockedByHardCap && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
              {costCapExceeded && (
                <p>Estimated cost {formatUsd(cost)} exceeds the hard cost cap of {formatUsd(costCap)}.</p>
              )}
              {tokenCapExceeded && (
                <p className={costCapExceeded ? 'mt-1' : ''}>
                  Estimated tokens {formatNumber(tokens)} exceed the token budget of {formatNumber(tokenCap)}.
                </p>
              )}
              <p className="mt-1">Update the firm standards and run Preview again before starting.</p>
            </div>
          )}

          {Boolean(startError) && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
              {errorMessage(startError)}
            </div>
          )}

          {requiresApproval && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              Estimated cost is above the approval threshold. Starting this run will queue the jobs immediately.
            </div>
          )}

          {hashHydration > 0 && (
            <div className="rounded-lg border border-lex-accent/20 bg-lex-accent-soft px-3 py-2 text-sm text-lex-accent">
              Stored attachments without checksums will be read once, fingerprinted with SHA-256, and then deduped before DMS ingest.
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={starting}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={starting || !canStart}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-lex-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-lex-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PlayIcon className="h-4 w-4" />
            {blockedByHardCap ? 'Blocked by Cap' : starting ? 'Starting' : 'Start Run'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RunHistory: React.FC<{
  runs: EmailEvidenceEmbeddingRun[];
  onStart: (run: EmailEvidenceEmbeddingRun) => void;
  onCancel: (run: EmailEvidenceEmbeddingRun) => void;
  starting: boolean;
  cancelling: boolean;
}> = ({ runs, onStart, onCancel, starting, cancelling }) => {
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  return (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[900px]">
      <thead>
        <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <th className="px-4 py-3">Created</th>
          <th className="px-4 py-3">Scope</th>
          <th className="px-4 py-3">Status</th>
          <th className="px-4 py-3 text-right">Bodies</th>
          <th className="px-4 py-3 text-right">Attachments</th>
          <th className="px-4 py-3 text-right">Tokens</th>
          <th className="px-4 py-3 text-right">Cost</th>
          <th className="px-4 py-3 text-right">Created</th>
          <th className="px-4 py-3 text-right">Progress</th>
          <th className="px-4 py-3 text-right">Action</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
        {runs.length === 0 ? (
          <tr>
            <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-600 dark:text-gray-400">
              No runs
            </td>
          </tr>
        ) : (
          runs.map((run) => {
            const expanded = expandedRunId === run.run_id;
            return (
            <React.Fragment key={run.run_id}>
            <tr
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750"
              onClick={() => setExpandedRunId(expanded ? null : run.run_id)}
              aria-expanded={expanded}
            >
              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatDate(run.created_at)}</td>
              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                <span className="inline-flex items-center gap-1.5">
                  <ChevronRightIcon className={`h-3.5 w-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                  {run.scope?.kind || 'scope'}
                </span>
              </td>
              <td className="px-4 py-3"><StatusBadge status={run.status} /></td>
              <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">{formatNumber(run.estimated_messages)}</td>
              <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">{formatNumber(run.estimated_attachments)}</td>
              <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">{formatNumber(run.estimated_embedding_tokens)}</td>
              <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">{formatUsd(run.estimated_cost_usd)}</td>
              <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">
                {formatNumber(runCreatedJobCount(run))}
              </td>
              <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">
                {runProgressLabel(run)}
              </td>
              <td className="px-4 py-3 text-right">
                {(run.status === 'previewed' && Number(run.estimated_jobs || 0) > 0) || canCancelRun(run) ? (
                  <div className="flex justify-end gap-1">
                    {run.status === 'previewed' && Number(run.estimated_jobs || 0) > 0 && (
                      <button
                        type="button"
                        disabled={starting}
                        onClick={(event) => {
                          event.stopPropagation();
                          onStart(run);
                        }}
                        className="inline-flex items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-lex-accent transition-colors hover:bg-lex-state-hover disabled:opacity-50"
                      >
                        <PlayIcon className="h-3.5 w-3.5" />
                        Start
                      </button>
                    )}
                    {canCancelRun(run) && (
                      <button
                        type="button"
                        disabled={cancelling}
                        onClick={(event) => {
                          event.stopPropagation();
                          onCancel(run);
                        }}
                        className="inline-flex items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-900/20"
                      >
                        <StopCircleIcon className="h-3.5 w-3.5" />
                        Cancel
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">-</span>
                )}
              </td>
            </tr>
            {expanded && (
              <RunDetailRow run={run} />
            )}
            </React.Fragment>
            );
          })
        )}
      </tbody>
    </table>
  </div>
  );
};

const RunDetailRow: React.FC<{ run: EmailEvidenceEmbeddingRun }> = ({ run }) => {
  const progress = run.run_progress;
  const failures = progress?.failure_breakdown || [];
  const active = Number(progress?.queued || 0) + Number(progress?.running || 0) + Number(progress?.retry_scheduled || 0);
  const skippedEntries = Object.entries(run.skip_counts || {}).filter(([, value]) => n(value) > 0);

  return (
    <tr className="bg-gray-50/80 dark:bg-gray-750/50">
      <td colSpan={10} className="px-4 py-4">
        <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Run Status</h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{runOutcomeLabel(run)}</p>
              </div>
              <StatusBadge status={run.status} />
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <MiniStat label="Created" value={formatNumber(runCreatedJobCount(run))} />
              <MiniStat label="Completed" value={formatNumber(progress?.completed || 0)} />
              <MiniStat label="Active" value={formatNumber(active)} />
              <MiniStat label="Failed" value={formatNumber(Number(progress?.failed || 0) + Number(progress?.blocked || 0))} />
              <MiniStat label="Message Bodies" value={`${formatNumber(progress?.body_total || 0)} / ${formatNumber(run.estimated_messages)}`} />
              <MiniStat label="Attachments" value={`${formatNumber(progress?.attachment_total || 0)} / ${formatNumber(run.estimated_attachments)}`} />
              <MiniStat label="Duplicates" value={formatNumber(run.duplicate_jobs)} />
              <MiniStat label="Completed At" value={formatDate(run.completed_at)} />
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Failure Details</h4>
            {failures.length === 0 ? (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">No job failures recorded for this run.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {failures.map((failure, index) => (
                  <div key={`${failure.source_kind}-${failure.error_code}-${index}`} className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm dark:border-red-900/50 dark:bg-red-900/10">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-red-800 dark:text-red-200">
                        {formatNumber(failure.count)} {failure.source_kind?.replace(/_/g, ' ') || 'job'} {failure.status}
                      </span>
                      <span className="text-xs text-red-700 dark:text-red-300">{failure.error_kind || failure.error_code || 'unknown'}</span>
                    </div>
                    <p className="mt-1 text-red-700 dark:text-red-300">{failure.error_message || failure.error_code || 'No failure message recorded.'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {skippedEntries.length > 0 && (
          <div className="mt-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Preview Scope Notes</h4>
            <div className="mt-3 flex flex-wrap gap-2">
              {skippedEntries.map(([key, value]) => (
                <span key={key} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  {humanizeRunLabel(key)}: {formatNumber(value)}
                </span>
              ))}
            </div>
          </div>
        )}
      </td>
    </tr>
  );
};

export default EvidenceIndexingView;
